import { Line, useAnimations } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import { findObjectStartsWithKey } from "../../../../utils/FindObjects";
import { useObjectCallback, useSheet } from "../../../../hooks/useTheatre";
import {
  AdditiveBlending,
  BufferGeometry,
  Euler,
  Line3,
  Matrix4,
  Quaternion,
  Raycaster,
  ShaderMaterial,
  Vector3,
  PointLight,
  Color,
  MathUtils,
} from "three";
import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import FresnelMaterial from "../../materials/FresnelMaterial";

import fragmentShaderReplacement from "../../shader/VertexColorHeat_Distance_replacement_frag.glsl";
import fragmentShaderAddition from "../../shader/VertexColorHeat_Distance_addition_frag.glsl";

import vertexShaderReplacement from "../../shader/VertexColorHeat_Distance_replacement_vert.glsl";
import vertexShaderAddition from "../../shader/VertexColorHeat_Distance_addition_vert.glsl";
import vertexShaderWeldacAddition from "../../shader/Weldac_Pipe_addition_vert.glsl";
import vertexShaderWeldacReplacement from "../../shader/Weldac_Pipe_replacement_vert.glsl";
import SteamParticlesInstances from "../hardline/SteamParticelsBAS";
import SparkParticlesInstances from "../effects/SparkParticlesInstances";
import { useDracoGLTF } from "@utils";
import { SideBarHider } from "@ui/SideBar/SideBar.jsx";
import SetCameraTarget from "../../SetCameraTarget.tsx";
import { MountWithEbbne, useEbbne } from "@solidwhite/ebbnen";

const LightName = "Weldac_Light_asdfadsfadsf";

const WeldacProcess = () => {
  const sheet = useSheet("EFD", "WELDAC_PROCESS", "0");
  const { nodes, scene, animations } = useDracoGLTF("./models/heat/Weldac_Process.glb");
  const { actions } = useAnimations(animations, scene);
  const object3d = scene;
  const action = actions["Anim_Weldac_process"];

  const { visible, active, select, selectable, layer, ebbneId } = useEbbne();

  const animationConfig = useMemo(() => {
    return {
      animationTime: 0,
      lightIntesity: 0,
      heatIntensity: 0,
      bendPosition: 0,
      pipePositionX: 0,
    };
  }, []);

  useObjectCallback(
    sheet,
    "Animation",
    animationConfig,

    ({ animationTime, lightIntesity, heatIntensity, bendPosition, pipePositionX }) => {
      action.time = sheet.sequence.position;
      if (Light) {
        Light.intensity = lightIntesity;
      }
      if (pipe.material.userData.shader) {
        pipe.material.userData.shader.uniforms.uHeat.value = heatIntensity * -1;
        pipe.material.userData.shader.uniforms.uBendUv.value = bendPosition;
        pipe.position.x = pipePositionX;
      }
    },
  );

  useEffect(() => {
    if (active) {
      action.reset().play().fadeIn(0.5);
      action.play();
      action.paused = true;
      sheet.sequence.position = 0;
      sheet.sequence.play({ iterationCount: Infinity });
    }
    return () => {
      sheet.sequence.position = 0;
      sheet.sequence.pause();
      action.time = 0;
      action.fadeOut(0.5).stop().reset();
    };
  }, [active]);

  var object = useMemo(() => {
    return findObjectStartsWithKey("Animation_Weldac_Process", object3d);
  }, []);

  const targetAnimationValue = useRef(0);
  const currentAnimationValue = useRef(0);

  const [lineStart, lineEnd] = useMemo(() => {
    var startObj = findObjectStartsWithKey("Weldacline_start", object3d);
    var startPos = new Vector3();
    startObj.getWorldPosition(startPos);

    var endObj = findObjectStartsWithKey("Weldacline_end", object3d);
    var endPos = new Vector3();
    endObj.getWorldPosition(endPos);
    return [startPos, endPos];
  }, []);

  const [pipe, heatPosition] = useMemo(() => {
    var workpiece = findObjectStartsWithKey("Weldac_Animation9_Weldac_Animation6_ME_Pipe", object3d);

    var heatSource = findObjectStartsWithKey("Heat_Source", object3d);

    var heatSourceWorld = new Vector3();
    heatSource.getWorldPosition(heatSourceWorld);

    var materialClone = workpiece.material.clone();
    // console.log(materialClone);
    // var materialClone = new MeshStandardMaterial();

    materialClone.uniforms = {
      uHeat: {
        value: 0.0,
      },
      uHeatFallof: {
        value: 0.9,
      },
      ...materialClone.uniforms,
    };

    materialClone.onBeforeCompile = (s) => {
      s.uniforms.uHeat = {
        value: 10.0,
      };
      s.uniforms.uHeatFallof = {
        value: 0.9,
      };
      s.uniforms.uHardened = {
        value: 0.0,
      };
      s.uniforms.uHeatSource = {
        value: heatSourceWorld,
      };
      s.uniforms.uHeatDistance = {
        value: 2,
      };
      s.uniforms.uBendUv = {
        value: 2,
      };
      s.uniforms.uBendValue = {
        value: 0.012,
      };
      s.uniforms.uBendLength = {
        value: 0.1,
      };
      s.uniforms.uTargetNormal = {
        value: new Vector3(0, 1, 0.7).normalize(),
      };
      s.vertexShader = `${vertexShaderWeldacAddition}\n${s.vertexShader}`;
      s.vertexShader = `${vertexShaderAddition}\n${s.vertexShader}`;
      s.vertexShader = s.vertexShader.replace(`#include <fog_vertex>`, vertexShaderReplacement);
      s.vertexShader = s.vertexShader.replace(`#include <project_vertex>`, vertexShaderWeldacReplacement);

      s.fragmentShader = `${fragmentShaderAddition}\n${s.fragmentShader}`;
      s.fragmentShader = s.fragmentShader.replace(`#include <color_fragment>`, ``);
      s.fragmentShader = s.fragmentShader.replace(`#include <dithering_fragment>`, fragmentShaderReplacement);

      materialClone.userData.shader = s;
    };

    workpiece.material = materialClone;

    return [workpiece, heatSourceWorld];
  }, []);

  const Light = useMemo(() => {
    var l = findObjectStartsWithKey(LightName, object3d);
    if (l) return l;

    var target = findObjectStartsWithKey("Heat_Source", object3d);

    var light = new PointLight(new Color("#ff0000"), 0);

    // light.add(debugSphere);
    light.position.copy(target.position);
    light.name = LightName;
    light.distance = 0.125;
    light.decay = 1;
    target.add(light);
    light.position.copy(new Vector3(0, 0.01, 0));

    return light;
  }, []);

  const handleDrag = (value) => {
    targetAnimationValue.current = value;
  };

  useFrame(() => {
    currentAnimationValue.current += (targetAnimationValue.current - currentAnimationValue.current) * 0.01;

    // action.time = action._clip.duration * currentAnimationValue.current * 0.4;

    // sheet.sequence.position =
    //   1.0 + action._clip.duration * currentAnimationValue.current * 0.4;
  });

  useEffect(() => {
    console.log(object);
    // object.visible = true;
    action.time = 0;
    action.play();
    action.fadeIn(0);
    action.paused = true;
    // console.log(object);
    return () => {
      // object.visible = false;
    };
  }, [object]);

  return (
    <>
      <MountWithEbbne mountedWhen={"visible"}>
        <primitive object={scene} />
      </MountWithEbbne>

      <MountWithEbbne mountedWhen={"active"}>
        {/* <mesh
        rotation={[0, MathUtils.degToRad(57), Math.PI / 2]}
        position={lineStart}
      >
        <cylinderBufferGeometry args={[0.035, 0.035, 2, 24]} />
        <meshStandardMaterial color={new Color(0.180760652,0.180760652,0.180760652)}
        metalness={0.95}
        roughness={0.485}
         />
      </mesh> */}
        {/* <SparkParticlesInstances active position={heatPosition} particleCount={50} lifetime={0.8}/> */}
        <SideBarHider />
        <SetCameraTarget settingsObject={findObjectStartsWithKey("CameraTarget", scene)} />
        <DragSphere lineStart={lineStart} lineEnd={lineEnd} onDrag={handleDrag} />
      </MountWithEbbne>
    </>
  );
};

useDracoGLTF.preload("./models/heat/Weldac_Process.glb");

const DragSphere = ({ lineStart = new Vector3(-1, 0, 0), lineEnd = new Vector3(1, 0, 0), onDrag = (value) => {}, ...props }) => {
  const debugPoint = useRef();
  const debugPointHit = useRef();
  const planeRef = useRef();

  const { raycaster } = useThree();

  const linePoints = useMemo(() => {
    return [lineStart, lineEnd];
  }, []);

  const lineGeometry = useMemo(() => {
    return new BufferGeometry().setFromPoints(linePoints);
  }, []);

  const virtualLine = useMemo(() => {
    var line = new Line3(linePoints[0], linePoints[1]);
    return line;
  }, []);

  const [planePosition, planeQuaternion] = useMemo(() => {
    var pos = new Vector3();
    pos.copy(lineStart);
    pos.add(lineEnd.clone().sub(lineStart).multiplyScalar(0.5));

    var lineDirection = lineEnd.clone().sub(lineStart);
    var matrix = new Matrix4().lookAt(lineStart, lineEnd, new Vector3(0, 1, 0));

    // console.log(lineDirection);

    const quaternion = new Quaternion();
    quaternion.setFromRotationMatrix(matrix);
    var rotate90 = new Quaternion().setFromEuler(new Euler(Math.PI / 2, Math.PI, Math.PI / 2));
    quaternion.multiply(rotate90);

    return [pos, quaternion];
  }, []);

  useFrame(() => {
    if (debugPoint.current) {
      // console.log(raycaster);
      var hit = raycaster.intersectObjects([planeRef.current], true);
      if (hit.length > 0) {
        var target = new Vector3();
        virtualLine.closestPointToPoint(hit[0].point, true, target);

        debugPointHit.current.position.copy(hit[0].point);
        debugPoint.current.position.copy(target);

        onDrag(virtualLine.closestPointToPointParameter(hit[0].point, true));
      }
    }
  });

  return (
    <group>
      <Line
        points={linePoints}
        transparent
        depthTest={false}
        depthWrite={false}
        color="orange"
        dashed={true}
        dashSize={0.01}
        gapSize={0.005}
        opacity={0.4}
        blending={AdditiveBlending}
        visible={false}
      />

      <mesh ref={debugPoint} visible={false}>
        <sphereGeometry args={[0.0075]} />
        <FresnelMaterial
          depthTest={false}
          depthWrite={false}
          opacity={0.8}
          transparent
          uBaseColor="orange"
          uFresnelColor="#ffe9a2"
          uFresnelPower={0.5}
          // blending={AdditiveBlending}
        />
      </mesh>

      <mesh ref={debugPointHit} visible={false}>
        <sphereGeometry args={[0.02]} />
        <meshBasicMaterial color="green" depthTest={false} depthWrite={false} transparent />
      </mesh>

      <group ref={planeRef} position={planePosition} quaternion={planeQuaternion}>
        <mesh renderOrder={11} visible={false}>
          <planeGeometry args={[2, 1]} />
          <meshBasicMaterial wireframe transparent color="yellow" depthTest={false} />
        </mesh>

        <mesh layers={[1]} visible={false}>
          <planeGeometry args={[2, 1]} />
          <meshBasicMaterial wireframe transparent color="yellow" />
        </mesh>
        <mesh layers={[1]} visible={false} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[2, 1]} />
          <meshBasicMaterial wireframe transparent color="yellow" />
        </mesh>
      </group>
    </group>
  );
};

export default WeldacProcess;
